import { gql } from "@apollo/client";
import {
  useHrisBaseQuery,
  useHrisProtectedIntegrationsQuery,
} from "./useHris.generated";
import { HrisConfigFragment } from "@src/features/settings/hris/setupFlow/OrgHrisConfigContext.generated";

type UseHrisOptions = {
  /**
   * When true (default), skips the queries that require manageOrgIntegrations permission.
   * Set to false only when the user has manageOrgIntegrations permission.
   */
  skipIntegrationQueries?: boolean;
};

type Return = {
  isIntegrated: boolean | undefined;
  provider: string | undefined;
  loading: boolean;
  hrisConfig: HrisConfigFragment | undefined;
  hasAppliedHrisCsvUpload: boolean | undefined;
  hasOrgSftpIntegration: boolean | undefined;
  hasRegularHrisIntegration: boolean | undefined;
  hasMergeBackfillRuns: boolean | undefined;
  hasHrisCsvUploads: boolean | undefined;
};

export const useHris = (options?: UseHrisOptions): Return => {
  // Default to skipping integration queries for safety
  const skipIntegrationQueries = options?.skipIntegrationQueries ?? true;

  // Base query - doesn't require special permissions
  const { data: baseData, loading: baseLoading } = useHrisBaseQuery({
    variables: {
      input: {
        filters: {},
      },
    },
  });

  // Integration query - requires manageOrgIntegrations permission
  const { data: integrationsData, loading: integrationsLoading } =
    useHrisProtectedIntegrationsQuery({
      variables: {
        sftpIntegrationsInput: {
          filters: {},
        },
        mergeBackfillRunsInput: {
          filters: {},
        },
        hrisCsvUploadsInput: {
          filters: {},
        },
      },
      skip: skipIntegrationQueries,
    });

  const loading =
    baseLoading || (!skipIntegrationQueries && integrationsLoading);

  return {
    isIntegrated: !!baseData?.HrisEmployees?.totalCount,
    provider: baseData?.Me?.user.org?.hrisProvider || undefined,
    loading,
    hrisConfig: baseData?.Me?.user.org?.hrisConfig || undefined,
    hasAppliedHrisCsvUpload: baseData?.Me?.user.org?.hasAppliedHrisCsvUpload,
    hasOrgSftpIntegration: skipIntegrationQueries
      ? undefined
      : (integrationsData?.OrgSftpIntegrations?.totalCount ?? 0) > 0 ||
        undefined,
    hasRegularHrisIntegration:
      (baseData?.Me?.user.org?.hrisIntegrations?.length ?? 0) > 0 || undefined,
    hasMergeBackfillRuns: skipIntegrationQueries
      ? undefined
      : (integrationsData?.MergeBackfillRuns?.totalCount ?? 0) > 0 || undefined,
    hasHrisCsvUploads: skipIntegrationQueries
      ? undefined
      : (integrationsData?.HrisCsvUploads?.totalCount ?? 0) > 0 || undefined,
  };
};

/**
 * Base HRIS query - fetches data that doesn't require special permissions.
 * This includes Me, HrisEmployees, and basic org data.
 */
gql`
  query HrisBase($input: HrisEmployeesInput!) {
    Me {
      user {
        id
        org {
          id
          hrisProvider
          hrisConfig {
            ...HrisConfig
          }
          hasAppliedHrisCsvUpload
          hrisIntegrations {
            id
          }
        }
      }
    }
    HrisEmployees(input: $input, pagination: { limit: 0 }) {
      totalCount
    }
  }
`;

/**
 * HRIS integrations query - fetches data that requires manageOrgIntegrations permission.
 * This includes OrgSftpIntegrations, MergeBackfillRuns, and HrisCsvUploads.
 */
gql`
  query HrisProtectedIntegrations(
    $sftpIntegrationsInput: OrgSftpIntegrationsInput!
    $mergeBackfillRunsInput: MergeBackfillRunsInput!
    $hrisCsvUploadsInput: HrisCsvUploadsInput!
  ) {
    OrgSftpIntegrations(
      input: $sftpIntegrationsInput
      pagination: { limit: 0 }
    ) {
      totalCount
    }
    MergeBackfillRuns(
      input: $mergeBackfillRunsInput
      pagination: { limit: 0 }
    ) {
      totalCount
    }
    HrisCsvUploads(input: $hrisCsvUploadsInput, pagination: { limit: 0 }) {
      totalCount
    }
  }
`;
